"use client";

import { useTrialForm } from "@/components/providers/TrialFormProvider";

/**
 * Inline call-to-action rendered in the middle of an article body.
 *
 * Authored explicitly with the Markdoc `{% cta /%}` tag, so writers control
 * placement. Rendered server-side (real DOM, good for SEO) as an <aside> inside
 * <article>; `not-prose` keeps the article's typography styles from leaking in.
 * The title is a styled <p>, not a heading, so the article's H2/H3 outline (and
 * its table of contents) stays clean.
 */
export default function InlineCoAnalystCta({ categories = [] }: { categories?: string[] }) {
  const isCompetitors = categories.includes("competitors");
  const { openTrialForm } = useTrialForm();

  return (
    <aside
      aria-label="Try the Co-Analyst"
      className="not-prose my-10 sm:my-12 relative overflow-hidden rounded-2xl bg-[#0a0a0a] text-white p-6 sm:p-8 md:p-9 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
    >
      <div className="relative z-10 max-w-xl">
        <p className="text-sm font-medium text-white/50 mb-3">
          Try the Co-Analyst
        </p>
        <p className="text-xl sm:text-2xl font-[family-name:var(--font-display)] tracking-[-0.02em] leading-[1.15] text-white">
          {isCompetitors
            ? "Do detailed peer analysis with high-precision AI"
            : "High-precision AI for institutional finance"}
        </p>
        <p className="mt-3 text-sm sm:text-[15px] leading-relaxed text-white/65 font-light">
          Hudson Labs delivers sourced, citeable research
          trusted by analysts, portfolio managers, and investors across 10,000+ public
          companies.
        </p>
      </div>

      <button
        type="button"
        onClick={openTrialForm}
        className="relative z-10 group inline-flex shrink-0 items-center gap-2 self-start md:self-auto rounded-lg bg-violet-600 hover:bg-violet-700 px-6 py-3.5 text-sm font-medium text-white transition-colors"
      >
        Start your trial
        <svg
          className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
        </svg>
      </button>
    </aside>
  );
}
